import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import {
  getDealsBalance,
  createPromotion,
  confirmPromotion,
  cancelPromotion,
  formatDealsAmount,
  type PromotionTier,
  type BalanceResponse,
} from '../../api/promotions';
import { toSigningPayload, getWalletProvider } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import SigningStepMonitor from '../trades/SigningStepMonitor';
import {
  FeatherX,
  FeatherStar,
  FeatherClock,
  FeatherCheck,
  FeatherAlertCircle,
  FeatherExternalLink,
} from '@subframe/core';

// Tier configurations
const TIERS: Record<PromotionTier, { icon: string; color: string }> = {
  '3_days': { icon: '3', color: 'bg-blue-100 text-blue-700' },
  '7_days': { icon: '7', color: 'bg-purple-100 text-purple-700' },
  '14_days': { icon: '14', color: 'bg-amber-100 text-amber-700' },
};

type DialogStage = 'select' | 'signing' | 'success' | 'error';

interface PromotionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  nftTokenId: string;
  nftName: string;
  userAddress: string;
  onSuccess?: () => void;
}

export const PromotionDialog: React.FC<PromotionDialogProps> = ({
  isOpen,
  onClose,
  nftTokenId,
  nftName,
  userAddress,
  onSuccess,
}) => {
  const [stage, setStage] = useState<DialogStage>('select');
  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState<BalanceResponse | null>(null);
  const [selectedTier, setSelectedTier] = useState<PromotionTier | null>(null);
  const [xamanPayload, setXamanPayload] = useState<SigningPayload | null>(null);
  const [processing, setProcessing] = useState(false);

  // Client-signing wallets (joey-wc) sign over a WC session — no QR, no Xaman polling.
  const isClientSigning = !!xamanPayload && getWalletProvider(xamanPayload.provider).capabilities.clientSigning;
  const [error, setError] = useState<string | null>(null);
  const [pollingCount, setPollingCount] = useState(0);

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxPollAttempts = 120; // 2 minutes at 1s interval

  // Fetch balance when dialog opens
  useEffect(() => {
    if (isOpen && userAddress) {
      fetchBalance();
    }
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, [isOpen, userAddress]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setStage('select');
      setSelectedTier(null);
      setXamanPayload(null);
      setProcessing(false);
      setError(null);
      setPollingCount(0);
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    }
  }, [isOpen]);

  const fetchBalance = async () => {
    setLoading(true);
    try {
      const response = await getDealsBalance(userAddress);
      if (response.success && response.data) {
        setBalanceData(response.data);
      } else {
        setError(response.error || 'Failed to fetch balance');
      }
    } catch (err) {
      setError('Failed to fetch $DEALS balance');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTier = (tier: PromotionTier) => {
    if (!balanceData?.tiers[tier].affordable) {
      return;
    }
    setSelectedTier(tier);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedTier || !balanceData?.tiers[selectedTier].affordable) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const response = await createPromotion({
        nftTokenId,
        tier: selectedTier,
      });

      if (response.success && response.data && (response.data.signingIntent || response.data.payload)) {
        const normalized = toSigningPayload(response.data.signingIntent ?? response.data.payload);
        setXamanPayload(normalized);
        setStage('signing');
        // Xaman resolves by polling confirmPromotion (which checks the on-chain payment).
        // Client-signing resolves via SigningStepMonitor; confirm runs once after signing.
        const clientSigning = getWalletProvider(normalized.provider).capabilities.clientSigning;
        if (!clientSigning) {
          startPolling(normalized.id);
        }
      } else {
        setError(response.error || 'Failed to create promotion');
      }
    } catch (err) {
      setError('Failed to create promotion request');
    } finally {
      setProcessing(false);
    }
  };

  const startPolling = (payloadUuid: string) => {
    setPollingCount(0);
    pollForStatus(payloadUuid);
  };

  const pollForStatus = async (payloadUuid: string) => {
    setPollingCount((prev) => prev + 1);

    try {
      const response = await confirmPromotion({ payloadUuid });

      if (response.success && response.data?.promotion) {
        setStage('success');
        if (pollTimerRef.current) {
          clearTimeout(pollTimerRef.current);
        }
        return;
      }

      if (response.error === 'Payment was rejected') {
        setError('Payment was rejected in Xaman');
        setStage('error');
        return;
      }

      // Keep polling if payment not yet completed
      if (pollingCount < maxPollAttempts) {
        pollTimerRef.current = setTimeout(() => pollForStatus(payloadUuid), 1000);
      } else {
        setError('Payment timed out. Please try again.');
        setStage('error');
      }
    } catch (err) {
      if (pollingCount < maxPollAttempts) {
        pollTimerRef.current = setTimeout(() => pollForStatus(payloadUuid), 1000);
      } else {
        setError('Failed to confirm payment');
        setStage('error');
      }
    }
  };

  const handleCancel = async () => {
    if (xamanPayload) {
      await cancelPromotion({ payloadUuid: xamanPayload.id });
    }
    onClose();
  };

  // Client-signing resolution: after the wallet signs+broadcasts (verified on-chain by
  // useWcSigningRequest), confirm the promotion once with the backend.
  const handleWcSigned = async () => {
    if (!xamanPayload) return;
    try {
      const response = await confirmPromotion({ payloadUuid: xamanPayload.id });
      if (response.success && response.data?.promotion) {
        setStage('success');
      } else {
        setError(response.error || 'Failed to confirm promotion');
        setStage('error');
      }
    } catch (err) {
      setError('Failed to confirm promotion');
      setStage('error');
    }
  };

  const handleDone = () => {
    onSuccess?.();
    onClose();
  };

  const handleOpenXaman = () => {
    if (xamanPayload?.deepLink) {
      window.open(xamanPayload.deepLink, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={stage === 'select' ? onClose : undefined}
      />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-md rounded-lg border border-solid border-neutral-border bg-default-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-solid border-neutral-border px-6 py-4">
          <div className="flex items-center gap-2">
            <FeatherStar className="h-5 w-5 text-amber-500" />
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              Feature Your NFT
            </h2>
          </div>
          <button
            onClick={stage === 'signing' ? handleCancel : onClose}
            className="rounded p-1 hover:bg-neutral-100 transition-colors"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Select Tier Stage */}
          {stage === 'select' && (
            <>
              {loading ? (
                <div className="flex flex-col items-center gap-4 py-8">
                  <LoadingSpinner size="md" />
                  <span className="text-body font-body text-subtext-color">
                    Loading your $DEALS balance...
                  </span>
                </div>
              ) : error && !balanceData ? (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500">
                    <FeatherAlertCircle className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Failed to Load
                    </span>
                    <span className="text-body font-body text-subtext-color text-center">{error}</span>
                  </div>
                  <Button variant="neutral-secondary" onClick={fetchBalance}>
                    Retry
                  </Button>
                </div>
              ) : balanceData ? (
                <div className="flex flex-col gap-6">
                  {/* Insufficient Balance Warning - at the top for visibility */}
                  {balanceData.affordable.length === 0 && (
                    <div className="flex items-start gap-3 rounded-lg bg-red-500 px-4 py-4">
                      <FeatherAlertCircle className="h-6 w-6 flex-shrink-0 text-white mt-0.5" />
                      <div className="flex flex-col gap-1">
                        <span className="text-body-bold font-body-bold text-white">
                          Insufficient $DEALS Balance
                        </span>
                        <span className="text-caption font-caption text-white/90">
                          You need at least 100K $DEALS to promote your NFT. Get $DEALS tokens at{' '}
                          <a
                            href="https://firstledger.net/token/rfnKKs998kvuVvJSW4jZ1MDxMSiCjhs9su/4445414C53000000000000000000000000000000"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white underline font-semibold hover:text-white/80"
                          >
                            FirstLedger
                          </a>
                          .
                        </span>
                      </div>
                    </div>
                  )}

                  {/* NFT Info */}
                  <div className="flex flex-col gap-1">
                    <span className="text-caption font-caption text-subtext-color">
                      Promoting
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {nftName || `NFT #${nftTokenId.slice(0, 8)}...`}
                    </span>
                  </div>

                  {/* Balance Display */}
                  <div className="flex items-center justify-between rounded-md bg-neutral-50 px-4 py-3">
                    <span className="text-body font-body text-subtext-color">
                      Your $DEALS Balance
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatDealsAmount(balanceData.balance)} $DEALS
                    </span>
                  </div>

                  {/* Info */}
                  <div className="text-caption font-caption text-subtext-color">
                    Featured NFTs appear first in collection listings regardless of filters or sorting.
                    Payment is made by burning $DEALS tokens.
                  </div>

                  {/* Tier Selection */}
                  <div className="flex flex-col gap-3">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Select Duration
                    </span>

                    {(['3_days', '7_days', '14_days'] as PromotionTier[]).map((tier) => {
                      const tierData = balanceData.tiers[tier];
                      const isAffordable = tierData.affordable;
                      const isSelected = selectedTier === tier;

                      return (
                        <button
                          key={tier}
                          onClick={() => handleSelectTier(tier)}
                          disabled={!isAffordable || processing}
                          className={`flex items-center justify-between rounded-md border-2 px-4 py-3 transition-all ${
                            isSelected
                              ? 'border-brand-600 bg-brand-50'
                              : isAffordable
                              ? 'border-neutral-border bg-default-background hover:border-brand-300 hover:bg-brand-50/50'
                              : 'border-neutral-border bg-neutral-100 opacity-60 cursor-not-allowed'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`flex h-10 w-10 items-center justify-center rounded-full ${TIERS[tier].color}`}
                            >
                              <FeatherClock className="h-5 w-5" />
                            </div>
                            <div className="flex flex-col items-start">
                              <span className="text-body-bold font-body-bold text-default-font">
                                {tierData.label}
                              </span>
                              <span className="text-caption font-caption text-subtext-color">
                                {tierData.description}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-body-bold font-body-bold text-default-font">
                              {formatDealsAmount(tierData.price)}
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              $DEALS
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {error && (
                    <div className="flex items-start gap-3 rounded-lg bg-red-500 px-4 py-4">
                      <FeatherAlertCircle className="h-5 w-5 flex-shrink-0 text-white mt-0.5" />
                      <span className="text-body font-body text-white">{error}</span>
                    </div>
                  )}
                </div>
              ) : null}
            </>
          )}

          {/* Signing Stage — Xaman (QR + confirmPromotion polling, unchanged) */}
          {stage === 'signing' && xamanPayload && !isClientSigning && (
            <div className="flex flex-col items-center gap-6 py-4">
              <div className="flex flex-col items-center gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Sign Transaction in Xaman
                </span>
                <span className="text-caption font-caption text-subtext-color text-center">
                  Scan the QR code with Xaman app to sign the payment
                </span>
              </div>

              {/* QR Code */}
              {xamanPayload.qrUrl && (
                <div className="rounded-lg border border-solid border-neutral-border p-4 bg-white">
                  <img
                    src={xamanPayload.qrUrl}
                    alt="Xaman QR Code"
                    style={{ width: 200, height: 200 }}
                  />
                </div>
              )}

              {/* Deep Link */}
              {xamanPayload.deepLink && (
                <Button
                  variant="neutral-secondary"
                  iconRight={<FeatherExternalLink />}
                  onClick={handleOpenXaman}
                >
                  Open in Xaman
                </Button>
              )}

              {/* Polling Status */}
              <div className="flex items-center gap-2 text-caption font-caption text-subtext-color">
                <LoadingSpinner size="sm" />
                <span>
                  Waiting for payment... ({pollingCount}/{maxPollAttempts})
                </span>
              </div>

              <Button variant="neutral-tertiary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          )}

          {/* Signing Stage — client-signing (joey-wc): approve prompt, no QR */}
          {stage === 'signing' && xamanPayload && isClientSigning && (
            <div className="flex flex-col items-center gap-6 py-4">
              <div className="flex flex-col items-center gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Approve in Your Wallet
                </span>
                <span className="text-caption font-caption text-subtext-color text-center">
                  Approve the $DEALS burn payment in your wallet to feature your NFT
                </span>
              </div>

              <LoadingSpinner size="md" />

              <SigningStepMonitor
                payload={xamanPayload}
                onSigned={() => { void handleWcSigned(); }}
                onRejected={() => { setError('Payment was rejected'); setStage('error'); }}
                onExpired={() => { setError('Payment request expired. Please try again.'); setStage('error'); }}
                onError={(msg) => { setError(msg); setStage('error'); }}
              />

              <Button variant="neutral-tertiary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          )}

          {/* Success Stage */}
          {stage === 'success' && (
            <div className="flex flex-col items-center gap-6 py-8">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                <FeatherCheck className="h-8 w-8 text-success" />
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Promotion Active!
                </span>
                <span className="text-body font-body text-subtext-color text-center">
                  Your NFT is now featured at the top of collection listings.
                </span>
              </div>
              <Badge variant="success">
                <FeatherStar className="h-3 w-3 mr-1" />
                Featured
              </Badge>
            </div>
          )}

          {/* Error Stage */}
          {stage === 'error' && (
            <div className="flex flex-col items-center gap-6 py-8">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500">
                <FeatherAlertCircle className="h-8 w-8 text-white" />
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Promotion Failed
                </span>
                <span className="text-body font-body text-subtext-color text-center">
                  {error || 'Something went wrong. Please try again.'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-solid border-neutral-border px-6 py-4">
          {stage === 'select' && (
            <>
              <Button variant="neutral-secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                onClick={handleSubmit}
                loading={processing}
                disabled={!selectedTier || !balanceData?.tiers[selectedTier]?.affordable || processing}
              >
                Burn $DEALS
              </Button>
            </>
          )}
          {stage === 'success' && (
            <Button variant="brand-primary" onClick={handleDone}>
              Done
            </Button>
          )}
          {stage === 'error' && (
            <>
              <Button variant="neutral-secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="brand-primary"
                onClick={() => {
                  setStage('select');
                  setError(null);
                  setSelectedTier(null);
                  fetchBalance();
                }}
              >
                Try Again
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
